import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import Reveal from "../components/motion/Reveal";
import {
  Sparkles, Check, Globe, Store, Mic, BellRing, Bot, TrendingUp, ArrowLeft,
  ArrowRight, Calendar, Rocket, Flag, CheckCircle2, Hammer,
} from "lucide-react";

const GLOBE_IMG = "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/91f959f8285f7c2e5ded63c872f1cf43d807e830f6f9479e1f0d8fb232c00298.png";

const CHAPTERS = [
  {
    id: "now",
    badgeIcon: CheckCircle2,
    badge: "Disponible maintenant",
    tone: "live",
    intro: "Les fondations sont posées. La plateforme est prête.",
    items: [
      { icon: Check, title: "Junago", desc: "Le logiciel complet : calendrier, réservations, contrats, signature, états des lieux, paiements, revenus et facturation." },
      { icon: Globe, title: "Votre site internet de réservation en ligne", desc: "Un site professionnel généré et hébergé par Junago : disponibilités en temps réel, paiement en ligne 24h/24, entièrement synchronisé avec votre logiciel." },
      { icon: Sparkles, title: "Junago Copilot", desc: "L'IA conversationnelle intégrée : interrogez toutes vos données, créez des réservations, gérez vos tâches — en langage naturel." },
    ],
  },
  {
    id: "dev",
    badgeIcon: Hammer,
    badge: "En développement",
    tone: "soon",
    intro: "La suite s'écrit déjà : votre marque rayonne, l'IA anticipe puis agit.",
    items: [
      { icon: Globe, title: "Nom de domaine personnalisé", desc: "Connectez votre propre domaine (www.votre-agence.fr) à votre site de réservation hébergé par Junago." },
      { icon: Store, title: "Place de marché Junago", desc: "Diffusez vos véhicules auprès de nouveaux clients, sans effort supplémentaire, depuis votre back office." },
      { icon: Mic, title: "Commandes vocales du Copilot", desc: "Parlez à Junago Copilot au lieu de taper : « Montre-moi mon planning », en voiture ou sur le parking." },
      { icon: BellRing, title: "Notifications proactives", desc: "Le Copilot vous alerte avant les problèmes : retard de retour, caution à renouveler, anomalie détectée." },
      { icon: Bot, title: "Agent IA autonome", desc: "L'IA n'assiste plus, elle agit : envoi des contrats, relance des devis, préparation des véhicules, planification des entretiens. Vous validez, elle exécute." },
      { icon: TrendingUp, title: "Pricing dynamique IA", desc: "Des tarifs ajustés automatiquement selon la demande, la saison et votre taux d'occupation, pour maximiser chaque véhicule." },
    ],
  },
];

const TONE = {
  live: { dot: "bg-emerald-400", pill: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30", icon: "text-emerald-400", iconBg: "bg-emerald-500/10" },
  soon: { dot: "bg-violet-400", pill: "bg-violet-500/10 text-violet-300 border-violet-400/30", icon: "text-violet-300", iconBg: "bg-violet-500/10" },
};

const ALL_CARDS = CHAPTERS.flatMap((ch, ci) => ch.items.map((it) => ({ ...it, ch: ci, tone: ch.tone })));
const CHAPTER_SWITCH = CHAPTERS[0].items.length - 0.7; // début du crossfade de chapitre
const OUTRO = 0.88; // dernière portion du scroll : fondu de sortie + conclusion

// Fallback statique (prefers-reduced-motion)
function StaticChapters() {
  return (
    <div className="max-w-5xl mx-auto px-6 pb-10 space-y-16">
      {CHAPTERS.map((ch, ci) => {
        const t = TONE[ch.tone];
        return (
          <div key={ch.id} data-testid={`roadmap-chapter-${ch.id}`}>
            <div className="flex items-center justify-center gap-2.5">
              <ch.badgeIcon className={`h-6 w-6 ${t.icon}`} />
              <div className="font-heading text-2xl md:text-4xl font-semibold text-white">{ch.badge}</div>
            </div>
            <p className="mt-3 text-sm text-slate-400 text-center italic">{ch.intro}</p>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {ch.items.map((it) => (
                <div key={it.title} className="rounded-2xl bg-white/[0.04] border border-white/10 p-6" data-testid={`roadmap-item-${it.title.slice(0, 20)}`}>
                  <div className={`h-10 w-10 rounded-xl ${t.iconBg} border border-white/10 flex items-center justify-center mb-4`}>
                    <it.icon className={`h-5 w-5 ${t.icon}`} />
                  </div>
                  <div className="font-heading text-base font-semibold text-white">{it.title}</div>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{it.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Roadmap() {
  const wrapRef = useRef(null);
  const contentRef = useRef(null);
  const trackRef = useRef(null);
  const barRef = useRef(null);
  const finalRef = useRef(null);
  const cardRefs = useRef([]);
  const headerRefs = useRef([]);
  const pausedRef = useRef(false);
  const renderedRef = useRef(0);

  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    document.title = "Roadmap Junago : construire le meilleur outil de location au monde";
    window.scrollTo(0, 0);
  }, []);

  // Moteur : le scroll vertical pilote le déplacement horizontal (lissé, pausable)
  useEffect(() => {
    if (reduced) return;
    const N = ALL_CARDS.length;

    const apply = (p) => {
      const track = trackRef.current;
      const cards = cardRefs.current;
      const first = cards[0];
      if (!track || !first) return;
      const w = first.offsetWidth;
      const step = cards[1] ? cards[1].offsetLeft - first.offsetLeft : w + 24;
      const cardProg = Math.min(p / OUTRO, 1) * (N - 1);

      track.style.transform = `translate3d(${window.innerWidth / 2 - w / 2 - cardProg * step}px,0,0)`;

      cards.forEach((el, i) => {
        if (!el) return;
        const prox = Math.max(0, 1 - Math.abs(cardProg - i));
        el.style.transform = `scale(${0.9 + 0.1 * prox})`;
        el.style.opacity = 0.32 + 0.68 * prox;
        el.classList.toggle("road-hcard-center", prox > 0.55);
        const body = el.querySelector("[data-body]");
        if (body) {
          body.style.opacity = 0.2 + 0.8 * prox;
          body.style.transform = `translateY(${(1 - prox) * 8}px)`;
        }
      });

      if (barRef.current) barRef.current.style.width = `${Math.min(p / OUTRO, 1) * 100}%`;

      // Crossfade échelonné entre les deux chapitres (jamais superposés)
      const t = Math.min(1, Math.max(0, (cardProg - CHAPTER_SWITCH) / 0.9));
      const h1 = headerRefs.current[0];
      const h2 = headerRefs.current[1];
      const o1 = Math.max(0, 1 - t * 2.2);
      const o2 = Math.max(0, (t - 0.55) / 0.45);
      if (h1) { h1.style.opacity = o1; h1.style.transform = `translateY(${t * -18}px)`; }
      if (h2) { h2.style.opacity = o2; h2.style.transform = `translateY(${(1 - t) * 18}px)`; }

      // Fin de narration : les cartes s'effacent, la conclusion apparaît
      const fade = Math.max(0, (p - OUTRO) / (1 - OUTRO));
      if (contentRef.current) contentRef.current.style.opacity = 1 - fade;
      if (finalRef.current) {
        finalRef.current.style.opacity = fade;
        finalRef.current.style.transform = `translateY(${(1 - fade) * 28}px)`;
      }
    };

    let raf;
    const loop = () => {
      const wrap = wrapRef.current;
      if (wrap) {
        const vh = window.innerHeight;
        const total = Math.max(1, wrap.offsetHeight - vh);
        const target = Math.min(1, Math.max(0, -wrap.getBoundingClientRect().top / total));
        if (!pausedRef.current) {
          const cur = renderedRef.current;
          renderedRef.current = Math.abs(target - cur) < 0.0004 ? target : cur + (target - cur) * 0.085;
          apply(renderedRef.current);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  const pauseProps = {
    onMouseEnter: () => { pausedRef.current = true; },
    onMouseLeave: () => { pausedRef.current = false; },
    onTouchStart: () => { pausedRef.current = true; },
    onTouchEnd: () => { pausedRef.current = false; },
  };

  return (
    <div className="min-h-screen bg-[#07070F] text-white">
      <PublicNavbar dark />

      {/* ─── Hero conquête du monde (inchangé) ─── */}
      <section className="relative -mt-16 pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden" data-testid="roadmap-hero">
        <div aria-hidden className="absolute inset-x-0 top-0 flex justify-center pointer-events-none">
          <img
            src={GLOBE_IMG}
            alt=""
            className="w-[1100px] max-w-none opacity-70 select-none"
            style={{ animation: "floatSlow 12s ease-in-out infinite", maskImage: "radial-gradient(ellipse 70% 70% at 50% 35%, black 40%, transparent 78%)", WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 35%, black 40%, transparent 78%)" }}
          />
        </div>
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-transparent via-[#07070F]/40 to-[#07070F] pointer-events-none" />
        <div aria-hidden className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.35) 1px, transparent 1px)", backgroundSize: "90px 90px" }} />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <Link to="/pourquoi-junago" className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-400 hover:text-white transition-colors mb-8" data-testid="roadmap-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Pourquoi Junago
            </Link>
          </Reveal>
          <Reveal delay={80}>
            <div className="flex items-center justify-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-violet-300">
              <Calendar className="h-3.5 w-3.5" /> Roadmap produit
            </div>
          </Reveal>
          <Reveal delay={160}>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08]" data-testid="roadmap-title">
              Notre ambition : le meilleur outil
              <br />
              de location <span className="text-gradient-animated">au monde.</span>
            </h1>
          </Reveal>
          <Reveal delay={260}>
            <p className="mt-6 text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Junago pilote votre activité. Junago Copilot vous assiste. Et demain, l&apos;IA agira pour vous.
              La plateforme gagne du terrain en permanence — voici l&apos;histoire, chapitre par chapitre.
            </p>
          </Reveal>
          <Reveal delay={340}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-2">
              {["Gestion complète", "Site de réservation", "IA intégrée", "Paiements", "Équipes"].map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-slate-300">
                  <Check className="h-3 w-3 text-emerald-400" strokeWidth={3} /> {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Roadmap narrative horizontale (le scroll vertical pilote le défilement) ─── */}
      {reduced ? (
        <StaticChapters />
      ) : (
        <section ref={wrapRef} style={{ height: `${ALL_CARDS.length * 55 + 150}vh` }} data-testid="roadmap-timeline">
          <div className="sticky top-0 h-screen overflow-hidden flex flex-col justify-center">
            <div ref={contentRef} className="will-change-[opacity]">
              {/* En-têtes de chapitres (crossfade) */}
              <div className="relative h-40 md:h-44 max-w-3xl mx-auto w-full">
                {CHAPTERS.map((ch, ci) => {
                  const t = TONE[ch.tone];
                  return (
                    <div
                      key={ch.id}
                      ref={(el) => { headerRefs.current[ci] = el; }}
                      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 will-change-transform"
                      style={{ opacity: ci === 0 ? 1 : 0 }}
                      data-testid={`roadmap-chapter-${ch.id}`}
                    >
                      <span className={`inline-block px-3 py-1 rounded-full border text-[10px] font-bold tracking-wide uppercase ${t.pill}`}>
                        Chapitre {ci + 1}
                      </span>
                      <div className="mt-3 flex items-center justify-center gap-2.5">
                        <ch.badgeIcon className={`h-6 w-6 ${t.icon}`} />
                        <div className="font-heading text-2xl md:text-4xl font-semibold text-white">{ch.badge}</div>
                      </div>
                      <p className="mt-3 text-sm text-slate-400 italic">{ch.intro}</p>
                    </div>
                  );
                })}
              </div>

              {/* Ligne de progression narrative */}
              <div className="max-w-xl mx-auto w-full px-6 mb-10 md:mb-12">
                <div className="h-[3px] bg-white/10 rounded-full overflow-hidden">
                  <div
                    ref={barRef}
                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-violet-500 to-fuchsia-500"
                    style={{ width: 0, boxShadow: "0 0 18px rgba(139,92,246,0.7)" }}
                    data-testid="roadmap-progress-line"
                  />
                </div>
              </div>

              {/* Piste horizontale */}
              <div ref={trackRef} className="flex gap-6 will-change-transform" style={{ width: "max-content" }}>
                {ALL_CARDS.map((it, i) => {
                  const t = TONE[it.tone];
                  return (
                    <div
                      key={it.title}
                      ref={(el) => { cardRefs.current[i] = el; }}
                      {...pauseProps}
                      className="road-hcard shrink-0 w-[78vw] sm:w-[380px] lg:w-[430px] rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-md p-6 md:p-8"
                      data-testid={`roadmap-item-${it.title.slice(0, 20)}`}
                    >
                      <span className={`inline-block px-2.5 py-0.5 rounded-full border text-[10px] font-bold tracking-wide uppercase ${t.pill}`}>
                        {CHAPTERS[it.ch].badge}
                      </span>
                      <div className={`road-hicon mt-5 h-11 w-11 rounded-xl ${t.iconBg} border border-white/10 flex items-center justify-center`}>
                        <it.icon className={`h-5 w-5 ${t.icon}`} />
                      </div>
                      <div data-body>
                        <div className="mt-4 font-heading text-lg md:text-xl font-semibold text-white">{it.title}</div>
                        <p className="mt-2 text-sm text-slate-400 leading-relaxed">{it.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Conclusion narrative (apparaît quand les cartes s'effacent) */}
            <div
              ref={finalRef}
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
              style={{ opacity: 0 }}
              data-testid="roadmap-final-node"
            >
              <span className="h-14 w-14 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-[0_16px_50px_-12px_rgba(168,85,247,0.8)]" style={{ animation: "nodePulse 2.4s ease-in-out infinite" }}>
                <Flag className="h-6 w-6 text-white" />
              </span>
              <div className="mt-6 font-heading text-3xl md:text-4xl font-medium tracking-tight">
                Et ce n&apos;est que le début.
              </div>
              <p className="mt-4 text-sm md:text-base text-slate-400 max-w-md mx-auto" data-testid="roadmap-final-sub">
                La Roadmap évolue en permanence grâce aux retours des loueurs et à notre vision produit.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ─── CTA final (design conservé, apparition premium) ─── */}
      <section className="max-w-5xl mx-auto px-6 pb-20 md:pb-28">
        <Reveal y={44}>
          <div
            className="rounded-3xl border border-white/10 bg-gradient-to-br from-violet-950/60 to-[#0B0B16] p-10 md:p-14 text-center relative overflow-hidden sheen-card"
            data-testid="roadmap-cta-block"
          >
            <div aria-hidden className="absolute -top-24 right-1/4 h-64 w-64 rounded-full bg-violet-600/30 blur-3xl" />
            <div className="relative">
              <Rocket className="h-8 w-8 text-violet-300 mx-auto mb-4" />
              <h2 className="font-heading text-2xl md:text-4xl font-medium tracking-tight text-white">
                Embarquez avant tout le monde.
              </h2>
              <p className="mt-3 text-sm md:text-base text-slate-400 max-w-lg mx-auto">
                Commencez votre essai gratuit et découvrez l&apos;avenir de la gestion de location.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register?context=vendor" className="btn-jn-light inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold" data-testid="roadmap-cta">
                  Démarrer l&apos;essai gratuit <ArrowRight className="btn-arrow h-4 w-4" />
                </Link>
                <button type="button" onClick={openInteractiveDemo} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors" data-testid="roadmap-cta-demo">
                  Voir la démo interactive
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
